/**
 * Print an inert HTML snapshot captured from the canvas runtime iframe.
 *
 * The live canvas iframe is sandboxed without `allow-same-origin` and runs
 * untrusted LLM-authored code, so it can't be printed directly (the parent
 * can't reach a null-origin `contentWindow.print()`, and granting the live
 * iframe `allow-modals` would hand untrusted code `window.print`/`alert`).
 *
 * Instead we drop the already-rendered snapshot — Tailwind's generated
 * <style> and the React output, with a `script-src 'none'` CSP stamped in —
 * into a throwaway iframe we control. It's `allow-same-origin` so the parent
 * can call `print()` on it, but crucially NOT `allow-scripts`: no snapshot
 * script runs, so the same-origin grant is inert. The browser's print dialog
 * paginates the document natively; the user picks "Save as PDF".
 */
export function printCanvasSnapshot({
  html,
  width,
}: {
  html: string;
  width: number;
}): void {
  const frame = document.createElement('iframe');
  // No `allow-scripts`: the snapshot is data, not code. `allow-same-origin`
  // lets the parent invoke print(); `allow-modals` permits the print() call.
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.setAttribute('aria-hidden', 'true');
  // Rendered (so the document lays out + paginates) but kept off-screen. A
  // real width keeps Tailwind responsive breakpoints resolving as they did
  // on screen rather than collapsing to a 0px viewport.
  frame.style.position = 'fixed';
  frame.style.top = '0';
  frame.style.left = '-10000px';
  frame.style.width = `${width || 900}px`;
  frame.style.height = '1200px';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';

  let cleanedUp = false;
  const cleanup = (): void => {
    if (cleanedUp) {
      return;
    }
    cleanedUp = true;
    frame.remove();
  };

  frame.addEventListener('load', () => {
    const win = frame.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    // Remove the frame once the print dialog closes. `print()` is synchronous
    // in most browsers, but `afterprint` covers the async ones; the timeout
    // is a backstop in case neither fires.
    win.addEventListener('afterprint', cleanup, {once: true});
    setTimeout(cleanup, 60_000);
    win.focus();
    win.print();
  });

  frame.srcdoc = html;
  document.body.appendChild(frame);
}
